import {
  Button,
  Callout,
  FormField,
  SkeletonText,
  SwitchField,
  Table,
} from '@dev-spendesk/grapes';
import { useFormik } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';

import { useCustomFieldsQuery } from 'modules/budgets/apis';
import { toApiFormat as toApiApprovalRules } from 'modules/company/structure/approval-flows/models';
import { QueryError } from 'src/core/common/components/QueryError';
import { QuerySuspense } from 'src/core/common/components/QuerySuspense';
import {
  type TGlobalFunctionTyped,
  useTranslation,
} from 'src/core/common/hooks/useTranslation';
import { useCompanyCurrency } from 'src/core/modules/app/hooks/useCompanyCurrency';
import { useCompanyId } from 'src/core/modules/app/hooks/useCompanyId';
import {
  NotificationType,
  useNotifications,
} from 'src/core/modules/app/notifications';
import { type CustomFieldDefinition } from 'src/core/modules/budgets/models/customFieldDefinition';
import { createApprovalScheme } from 'src/core/modules/company/cost-centers/containers/CostCenterFormModalContainer/hooks/createApprovalScheme';
import { editApprovalScheme } from 'src/core/modules/company/cost-centers/containers/CostCenterFormModalContainer/hooks/editApprovalSchemeQuery';
import { type ApprovalRule } from 'src/core/modules/company/structure/approval-flows';

import { validateForm } from './validate';
import { defaultApprovalRules } from '../../approvalScheme';
import { type Dimension } from '../../approvalWorkflow';
import { APPROVAL_SCHEMES_QUERY_KEY } from '../../hooks/useApprovalSchemesQuery';
import { useGetDimensions } from '../../hooks/useGetDimensions';
import { type CustomNode } from '../../node';
import { useWorkflowContext } from '../../state/WorkflowContext';
import { ApprovalScheme } from '../ApprovalScheme';
import { DimensionSelect, getDimensionOptions } from '../DimensionSelect';
import { DimensionValuesSelect } from '../DimensionValuesSelect';

type Props = {
  approvalRules?: ApprovalRule[];
  dimension?: Dimension;
  selectedValues?: { id: string; name: string }[] | null | undefined;
  approvalSchemeId: string | undefined;
  mode: 'create' | 'edit';
  onCancel: () => void;
  onSubmit: () => void;
};

export type FormProps = {
  dimension: Dimension | undefined;
  values: { id: string; name: string }[] | null | undefined;
  approvalRules: ApprovalRule[] | undefined;
};

export const getDimensionOptionValue = ({
  dimension,
  customFields,
  t,
  dimensions,
}: {
  dimension: Dimension | undefined;
  customFields: CustomFieldDefinition[];
  t: TGlobalFunctionTyped;
  dimensions: Dimension[];
}) => {
  return getDimensionOptions(t, dimensions, customFields).find(
    (dimensionOption) => {
      if (dimension?.type === 'customField') {
        return dimensionOption.key === dimension?.customFieldId;
      }
      return dimensionOption.key === dimension?.type;
    },
  );
};

export const DimensionApprovalFlowPanelContent = ({
  onCancel,
  onSubmit,
  approvalRules,
  approvalSchemeId,
  dimension,
  selectedValues,
  mode,
}: Props) => {
  const companyId = useCompanyId();
  const { t } = useTranslation('global');
  const dimensions = useGetDimensions();
  const customFieldsQueryState = useCustomFieldsQuery();
  const panelReference = useRef<HTMLDivElement | null>(null);
  const { pushNotif } = useNotifications();
  const queryClient = useQueryClient();
  const {
    nodes,
    edges,
    setNodes,
    selectedNodeId,
    isLeafNode,
    handleSubmitApprovalFlow,
    getAllParentNodes,
    getSiblingNodes,
  } = useWorkflowContext();
  const [isApprovalSchemeSetupOpened, setIsApprovalSchemeSetupOpened] =
    useState(Boolean(approvalRules && approvalRules.length > 0));
  const currency = useCompanyCurrency();

  useEffect(() => {
    setIsApprovalSchemeSetupOpened(
      Boolean(approvalRules && approvalRules.length > 0),
    );
  }, [approvalRules]);

  const sibling = getSiblingNodes(selectedNodeId ?? '').find(
    (node) => node.type === 'baseNode' && node.id !== selectedNodeId,
  );
  const siblingDimension =
    sibling?.type === 'baseNode' ? sibling.data.dimension : undefined;

  const {
    values: formValues,
    errors,
    setFieldValue,
    handleSubmit,
  } = useFormik<FormProps>({
    enableReinitialize: true,
    initialValues: {
      dimension: getInitialDimension(dimension, siblingDimension),
      values: selectedValues,
      approvalRules,
    },
    validateOnChange: false,
    validate: (values) =>
      validateForm(values, isApprovalSchemeSetupOpened, panelReference, t),
    onSubmit: async (values, { setSubmitting }) => {
      let updatedNodes: CustomNode[] = nodes.map((node) => {
        if (node.id === selectedNodeId && node.type === 'baseNode') {
          return {
            ...node,
            data: {
              ...node.data,
              dimension: values.dimension,
              values: values.values,
            },
          };
        }
        return node;
      });

      // case when the user toggles off the approval scheme setup
      if (
        !values.approvalRules &&
        !isApprovalSchemeSetupOpened &&
        approvalSchemeId
      ) {
        updatedNodes = updatedNodes.map((node) => {
          if (node.id === selectedNodeId && node.type === 'baseNode') {
            return {
              ...node,
              data: {
                ...node.data,
                schemeId: undefined,
              },
            };
          }
          return node;
        });
      }

      try {
        setNodes(updatedNodes);
        await handleSubmitApprovalFlow(updatedNodes, edges);

        if (!values.approvalRules) {
          setSubmitting(false);
          onSubmit();
          return;
        }

        if (approvalSchemeId) {
          await editApprovalScheme({
            companyId,
            payload: {
              rules: toApiApprovalRules(values.approvalRules),
            },
            approvalSchemeId,
          });
          queryClient.invalidateQueries(APPROVAL_SCHEMES_QUERY_KEY);
        } else {
          const response = await createApprovalScheme(
            {
              owner: null,
              rules: toApiApprovalRules(values.approvalRules),
            },
            companyId,
          );

          // Assign new approval scheme id to this node
          const updatedNodesWithSchemeId: CustomNode[] = updatedNodes.map(
            (node) => {
              if (node.id === selectedNodeId && node.type === 'baseNode') {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    schemeId: response.id,
                  },
                };
              }
              return node;
            },
          );
          setNodes(updatedNodesWithSchemeId);
          await handleSubmitApprovalFlow(updatedNodesWithSchemeId, edges);
          queryClient.invalidateQueries(APPROVAL_SCHEMES_QUERY_KEY);
        }
        setSubmitting(false);
        onSubmit();
      } catch (error) {
        pushNotif({
          type: NotificationType.Danger,
          message:
            error instanceof Error
              ? error.message
              : t('approvalWorkflows.notifications.error'),
        });
        setSubmitting(false);
        throw error;
      }
    },
  });

  const handleIsApprovalSchemeSetupOpenedChange = (isOpen: boolean) => {
    setIsApprovalSchemeSetupOpened(isOpen);
    if (!isOpen) {
      setFieldValue('approvalRules', undefined);
    }
  };

  const currentNode = nodes.find((node) => node.id === selectedNodeId);
  const currentNodeHasScheme =
    currentNode?.type === 'baseNode' &&
    currentNode?.data.schemeId !== undefined;
  const shouldShowNoSchemeWarning =
    isLeafNode(selectedNodeId ?? '') && !currentNodeHasScheme;

  const allParentNodesHaveScheme = getAllParentNodes(
    selectedNodeId ?? '',
  ).every(
    (node) => node.type === 'baseNode' && node.data.schemeId !== undefined,
  );

  return (
    <QuerySuspense
      queryState={customFieldsQueryState}
      loading={<LoadingSkeleton />}
      fallback={(error) => (
        <QueryError componentType="Callout" queryError={error} />
      )}
    >
      {(customFields) => (
        <>
          <div className="grow overflow-y-auto" ref={panelReference}>
            <div className="p-32 pb-0">
              <FormField
                className="text-left"
                label={t('approvalWorkflows.panel.when')}
              >
                <DimensionSelect
                  value={getDimensionOptionValue({
                    dimension: formValues.dimension,
                    customFields: customFields.customFields,
                    t,
                    dimensions,
                  })}
                  onChange={(value) => {
                    if (value.type === 'customField') {
                      setFieldValue('dimension', {
                        type: 'customField',
                        customFieldId: value.customFieldId,
                      });
                    } else {
                      setFieldValue('dimension', { type: value.type });
                    }
                  }}
                />
              </FormField>
              {formValues.dimension !== undefined && (
                <div className="mt-16">
                  <DimensionValuesSelect
                    mode={mode}
                    dimension={formValues.dimension}
                    selectedValues={formValues.values}
                    onSelect={(values) => setFieldValue('values', values)}
                  />
                </div>
              )}
              {formValues.values &&
                formValues.values.length > 0 &&
                formValues.dimension && (
                  <>
                    <div className="title-m mt-8 mb-16 lowercase">
                      {t('approvalWorkflows.panel.valuesSelected', {
                        count: formValues.values?.length,
                      })}
                    </div>
                    <Table
                      className="[&_thead]:hidden"
                      rowHeight="compact"
                      maxHeight={300}
                      data={formValues.values.map(
                        (value: { id: string; name: string }) => ({
                          id: value.id,
                          name: value.name,
                        }),
                      )}
                      columns={[
                        {
                          id: 'name',
                          header: 'Value',
                          renderCell: (value) => value.name,
                        },
                      ]}
                    />
                  </>
                )}
              {errors.values && (
                <Callout
                  title={errors.values}
                  variant="alert"
                  className="mt-16"
                />
              )}
              <div className="bg-background-primary-pressed my-16 h-[1px] w-full" />
              <SwitchField
                fit="parent"
                label={t('approvalWorkflows.approvalFlow.setUpAnApprovalFlow')}
                isChecked={isApprovalSchemeSetupOpened}
                onChange={(event) => {
                  handleIsApprovalSchemeSetupOpenedChange(event.target.checked);
                }}
              />
            </div>
            {shouldShowNoSchemeWarning && !isApprovalSchemeSetupOpened && (
              <Callout
                title={getCalloutTitle(!allParentNodesHaveScheme, t)}
                variant="warning"
                className="mx-32 my-16"
              >
                {getCalloutDescription(!allParentNodesHaveScheme, t)}
              </Callout>
            )}
            {isApprovalSchemeSetupOpened && (
              <div className="p-32 pt-16 pb-[140px]">
                <ApprovalScheme
                  error={
                    errors.approvalRules
                      ? (errors.approvalRules as string)
                      : undefined
                  }
                  onChange={(rules) => setFieldValue('approvalRules', rules)}
                  rules={
                    formValues.approvalRules ?? defaultApprovalRules(currency)
                  }
                />
              </div>
            )}
          </div>
          <div className="rounded-bl-12 rounded-br-12 border-t-border-default bg-background-primary-default absolute bottom-0 z-10 flex w-full justify-between gap-16 border-0 border-t border-solid px-32 py-16">
            <Button
              text={t('approvalWorkflows.approvalFlow.cancel')}
              className="flex-grow"
              variant="secondaryNeutral"
              onClick={() => {
                onCancel();
              }}
            />
            <Button
              text={t('approvalWorkflows.approvalFlow.save')}
              className="flex-grow"
              variant="primaryBrand"
              onClick={() => {
                handleSubmit();
              }}
            />
          </div>
        </>
      )}
    </QuerySuspense>
  );
};

const getCalloutTitle = (noSchemeInPath: boolean, t: TGlobalFunctionTyped) => {
  if (noSchemeInPath) {
    return t('approvalWorkflows.panel.noSchemeWarningPath.title');
  }
  return t('approvalWorkflows.panel.noSchemeWarning.title');
};

const getCalloutDescription = (
  noSchemeInPath: boolean,
  t: TGlobalFunctionTyped,
) => {
  if (noSchemeInPath) {
    return t('approvalWorkflows.panel.noSchemeWarningPath.description');
  }
  return t('approvalWorkflows.panel.noSchemeWarning.description');
};

const getInitialDimension = (
  dimension: Dimension | undefined,
  siblingDimension: Dimension | undefined,
): Dimension | undefined => {
  if (siblingDimension) {
    return siblingDimension;
  }

  return dimension;
};

const LoadingSkeleton = () => {
  return (
    <div className="flex h-full w-full items-center justify-center">
      <SkeletonText size="xxl" width="full" />
    </div>
  );
};
